import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { ItemResponse, MailDraftSettings, SaveMailDraftSettingsInput } from '@proerp/shared';
import { apiRequest } from '@/lib/apiClient';

/** Ziel des Menüpunkts „Outlook-Entwurf einrichten" in den Beleg-Editoren (Tab in SettingsPage). */
export const MAIL_DRAFT_SETTINGS_HREF = '/einstellungen?tab=mail-entwurf';

/**
 * Gemeinsamer Präfix aller Abfragen rund um den Mail-Entwurf (Einstellungen, Status in
 * Verkauf und Einkauf). Wer die Einrichtung ändert, macht alles darunter ungültig.
 */
export const MAIL_DRAFT_KEY = ['mail-draft'] as const;
const SETTINGS_KEY = [...MAIL_DRAFT_KEY, 'settings'] as const;
/** Schlüssel der Einrichtung unter Formeln & Vorlagen (Angebotskalkulation) — dieselbe Zeile. */
const QUOTE_CALC_MAIL_SETTINGS_KEY = ['quote-calc-mail-settings'] as const;

/** Nach dem Speichern: Einstellungen, Beleg-Status und die Kalkulations-Sicht neu laden. */
export function invalidateMailDraftQueries(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: MAIL_DRAFT_KEY });
  void queryClient.invalidateQueries({ queryKey: QUOTE_CALC_MAIL_SETTINGS_KEY });
}

/** Einrichtung des Mail-Entwurfs (ADR 0113, Nachtrag) — die Flow-URL kommt nie zurück. */
export function useMailDraftSettings({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: () => apiRequest<ItemResponse<MailDraftSettings>>('/v1/mail-draft-settings'),
    enabled,
  });
}

export function useSaveMailDraftSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SaveMailDraftSettingsInput) =>
      apiRequest<ItemResponse<MailDraftSettings>>('/v1/mail-draft-settings', { method: 'PUT', body: JSON.stringify(input) }),
    onSuccess: (response) => {
      queryClient.setQueryData(SETTINGS_KEY, response);
      invalidateMailDraftQueries(queryClient);
    },
  });
}
